import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { categories as mockCategories } from '../data/products';

interface Category {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  image_url: string | null;
  product_count: number;
  subcategories?: Subcategory[];
}

interface Subcategory {
  id: string;
  category_id: string;
  name: string;
  slug: string;
  description: string | null;
  product_count: number;
}

export const useCategories = () => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setLoading(true);
        
        // Try to fetch from database first
        try {
          // Fetch categories
          const { data: categoriesData, error: categoriesError } = await supabase
            .from('categories')
            .select('*')
            .order('name');
          
          if (categoriesError) throw categoriesError;
          
          if (categoriesData && categoriesData.length > 0) {
            // Fetch subcategories
            const { data: subcategoriesData, error: subcategoriesError } = await supabase
              .from('subcategories')
              .select('*')
              .order('name');
            
            if (subcategoriesError) throw subcategoriesError;
            
            // Group subcategories by category_id
            const categoriesWithSubcategories = categoriesData.map((category: Category) => ({
              ...category,
              subcategories: subcategoriesData.filter(
                (subcategory: Subcategory) => subcategory.category_id === category.id
              )
            }));
            
            setCategories(categoriesWithSubcategories);
          } else {
            // Use mock data if no database categories
            setCategories(mockCategories);
          }
        } catch (dbError) {
          console.warn('Database not available, using mock data:', dbError);
          setCategories(mockCategories);
        }
      } catch (err) {
        console.error('Error fetching categories:', err);
        // Fallback to mock data on any error
        setCategories(mockCategories);
      } finally {
        setLoading(false);
      }
    };

    fetchCategories();
  }, []);

  return { categories, loading, error };
};